/**
 * BYOB bundle: ordered `picks[]`, capacity = pack size (`pb:pack-size-change`).
 * Scopes to `[data-pb-byob-bundle-root]`. Requires `pb-bundle-flavor-card` to skip clicks inside root.
 * Build CTA `[data-pb-byob-build-cta]`: adds picks to cart (subscribe vs one-time from `.build-your-box__frequency`).
 * Delivery wrap `[data-pb-byob-frequency-delivery-note]` (and `-mobile`): delivery pill + optional micro line; hidden when one-time is selected; shown for subscribe (default).
 * Picker title row: `.build-your-box__picker-title-row--scrolled` toggles when `.build-your-box__picker-grid` `scrollTop > 0` (scroll affordance).
 *
 * Progress bar: two-stop `linear-gradient` on `.pb-byob__selected-progress-track` (see `_syncProgressVisual`; avoids `scaleX` + overflow quirks in WebKit).
 * Debug logs: add `?pb_byob_debug=1` to the URL or run `localStorage.setItem('pb_byob_debug','1')` and reload.
 *
 * Cart: Build CTA POSTs `/cart/add.js` with aggregated line items. Subscribe mode
 * includes `selling_plan`; one-time omits it. Subscribe + valid pack applies cart discount code
 * `BYOB{N}-<suffix>` via `/cart/update.js` **before** add (so UpCart’s post-add refresh already
 * sees the code). Codes come from section settings on the bundle root:
 * `data-pb-byob-cart-discount-{2|3|4|6}`; suffix fallback `PB_BYOB_DISCOUNT_CODE_SUFFIX` if unset.
 * Subscribe Step 3 blue price uses anchor-adjusted sale when subscribe-pack % > 0 and anchor subtotal exists (`_totalsForFrequencyLabels`).
 *
 * Mobile sheet (≤989px, `build-your-box`): sticky panel includes a duplicate frequency UI +
 * full-width ATC; rail + mobile fieldsets stay mirrored. Completing the pack auto-opens the
 * drawer on narrow viewports even when the in‑page summary panel is visible; while the drawer
 * is open (or transitioning closed), the sticky chrome stays on-screen so scrolling does
 * not immediately dismiss it. Backdrop tap closes (PB‑22). After a successful add, the sheet/backdrop closes
 * and the cart drawer opens on the next task. Line totals use
 * `data-pb-onetime-price-cents` / `data-pb-subscribe-price-cents` on grid cells; optional strike
 * line uses `data-pb-onetime-compare-price-cents` / `data-pb-subscribe-compare-price-cents` when higher,
 * or verbatim Subscribe strike strings (`frequency_subscribe_compare`) when Step 3 merchandising overrides are active.
 *
 * Narrow viewports (<990px theme CSS): the rail headline price banner (`.build-your-box__rail-price-summary`) stays hidden; Step 3 frequency row prices + pack star badges remain visible.
 * `[data-pb-byob-subscribe-pack-savings]`: Subscribe pink **Save $X** from Step 3 anchor × pack × subscribe-pack % (`data-pb-byob-subscribe-discount-pack-{n}`, decimal-friendly).
 * `[data-pb-byob-onetime-pack-savings]`: Step 3 One-time — **Save $X** = strike − sale (`_totalsForFrequencyLabels`). %-off uses Σ floor((each slot’s catalogue cents × pct) / 100) to track Shopify automatic discounts (avoids round(bundle × pct), e.g. $7.98 vs $8 on two × $39.99 @ 10%).
 *
 * Category pills (`.build-your-box__product-category-filters` `[data-pb-byob-category]`) show/hide
 * grid cells via the `hidden` attribute using `data-pb-byob-tags` (**Shopify tags only**, lowercased in Liquid).
 * Aliases in `PB_BYOB_CATEGORY_ALIASES` absorb common singular/plural and hyphen/spacing variants; v1: hiding does not clear picks.
 * Narrow layouts: category row scrolls horizontally; thin styled scrollbar + JS toggles
 * `.build-your-box__product-category-filters-outer--hint-left|right` for edge gradients when more content lies that way.
 * Tabs for a category with **no** matching tagged products are `hidden` on load (Best Sellers always stays).
 * Post-CTA mix card `.pb-byob-post-cta__mix-card`: row visibility mirrors category pills (grid + tags); per-category counts + `other` from picks/tags so totals match pick count (see `_syncPostCtaMixCategoryVisibility`, `_syncPostCtaMixCounts`).
 *
 * Per-pack attrs on the bundle root: one-time (`data-pb-byob-onetime-discount-pack-2` … `-6`) for OTC strike/sale; subscribe (`data-pb-byob-subscribe-discount-pack-2` … `-6`) as **text %** for Subscribe pink Save + displayed Subscribe total (anchor × pack when anchor > 0). Cart/checkout uses Shopify prices separately.
 * =============================================================================
 */
(function () {
  'use strict';

  var MAX_PACK = 6;
  var VALID_PACK = { 2: true, 3: true, 4: true, 6: true };

  /**
   * Fallback when `[data-pb-byob-cart-discount-{n}]` is missing or blank (e.g. older section HTML).
   * Format: BYOB{N}-<suffix>; keep in sync with theme section defaults.
   */
  var PB_BYOB_DISCOUNT_CODE_SUFFIX = '2RMD0B3NPCJA';

  /** Desktop (≥990px) `--pb-byob-selected-cols-desktop`: 2/3/4 = one row; 6 = 3 cols × 2 rows (CSS). */
  function pbByobDesktopSummaryCols(packSize) {
    if (packSize === 6) return 3;
    return packSize;
  }

  /**
   * Pill `data-pb-byob-category` keys → acceptable **tag** tokens (not product type). Match: exact token, or same as alias
   * after normalizing hyphens/spaces (covers e.g. Brownie vs brownies once tags are lowercased in Liquid).
   */
  var PB_BYOB_CATEGORY_ALIASES = {
    muffins: ['muffin', 'muffins'],
    brownies: ['brownie', 'brownies'],
    rice_treats: [
      'rice-treat',
      'ricetreat',
      'rice-treats',
      'ricetreats',
      'rice treat',
      'rice treats',
      'rice_treat',
      'rice_treats',
    ],
    variety_packs: [
      'variety',
      'variety-pack',
      'variety-packs',
      'variety pack',
      'variety packs',
      'variety box',
      'variety-box',
      'variety_box',
      'varietybox',
      'variety boxes',
      'variety-boxes',
      'variety_boxes',
      'varietyboxes',
      'varietypack',
      'varietypacks',
      'variety_pack',
      'variety_packs',
    ],
  };
  /** Selected-summary type line: one bucket per pick (first matching category in this order). */
  var PB_BYOB_CATEGORY_BREAKDOWN_ORDER = [
    { key: 'brownies', label: 'Brownies' },
    { key: 'rice_treats', label: 'Rice treats' },
    { key: 'muffins', label: 'Muffins' },
    { key: 'variety_packs', label: 'Variety packs' },
  ];
  var PB_BYOB_PROGRESS_TRACK = '#d0d0d0';
  var PB_BYOB_PROGRESS_FILL = '#e63f82';
  /** Best Sellers tab: show only the first N eligible products in collection order (admin sort, e.g. Best selling). */
  var PB_BYOB_BEST_SELLERS_TAB_CAP = 8;

  /** Opt-in: `?pb_byob_debug=1` on the page URL or `localStorage.setItem('pb_byob_debug','1')` then reload. */
  function pbByobDebugEnabled() {
    try {
      if (typeof window.location !== 'undefined' && String(window.location.search).indexOf('pb_byob_debug=1') !== -1) {
        return true;
      }
      if (typeof window.localStorage !== 'undefined' && window.localStorage.getItem('pb_byob_debug') === '1') {
        return true;
      }
    } catch (e) {}
    return false;
  }

  var REMOVE_SVG =
    '<span class="pb-byob__selected-remove-icon" aria-hidden="true">' +
    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">' +
    '<path d="M20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20C15.5228 20 20 15.5228 20 10Z" fill="#3A0E11"/>' +
    '<path d="M11.5957 10.006L14.0001 12.4104L12.5859 13.8246L10.1814 11.4202L7.777 13.8246L6.36279 12.4104L8.76723 10.006L6.36348 7.6022L7.77769 6.18799L10.1814 8.59174L12.5852 6.18799L13.9994 7.6022L11.5957 10.006Z" fill="white"/>' +
    '</svg></span>';

  var EMPTY_PLUS_SVG =
    '<span class="pb-byob__selected-empty-icon" aria-hidden="true">' +
    '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">' +
    '<path d="M10 5H0" stroke="#3A0E11" stroke-width="2.5"/>' +
    '<path d="M5 0V10" stroke="#3A0E11" stroke-width="2.5"/>' +
    '</svg></span>';

  function escapeAttr(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /** @param {number} cents */
  function pbByobFormatMoneyCents(cents) {
    var n = typeof cents === 'number' ? cents : parseInt(String(cents), 10);
    if (isNaN(n) || n < 0) n = 0;
    var dollars = n / 100;
    try {
      var cur =
        typeof window.Shopify !== 'undefined' &&
        window.Shopify.currency &&
        window.Shopify.currency.active
          ? String(window.Shopify.currency.active)
          : 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(dollars);
    } catch (err) {
      return '$' + dollars.toFixed(2);
    }
  }

  function readPackSizeFromFieldset(fieldset) {
    if (!fieldset) return 2;
    var input = fieldset.querySelector('.pb-pack-selector__input:checked');
    if (!input) return 2;
    var n = parseInt(String(input.value), 10);
    return VALID_PACK[n] ? n : 2;
  }

  function pbByobNormTagToken(s) {
    return String(s || '')
      .trim()
      .toLowerCase()
      .replace(/-/g, '')
      .replace(/_/g, '')
      .replace(/\s+/g, '');
  }

  /** @param {string} flavorTitle Strip from `data-pb-flavor-title` — used when variety packs have no matching tag yet title says "Variety". */
  function pbByobCellMatchesCategory(tagsCsv, categoryKey, flavorTitle) {
    if (!categoryKey || categoryKey === 'best_sellers') return true;
    var aliases = PB_BYOB_CATEGORY_ALIASES[categoryKey];
    if (!aliases || !aliases.length) return false;
    var flavor = flavorTitle || '';
    var parts = String(tagsCsv || '')
      .split(',')
      .map(function (p) {
        return p.trim().toLowerCase();
      })
      .filter(Boolean);
    if (parts.length > 0) {
      for (var a = 0; a < aliases.length; a++) {
        var alias = String(aliases[a]).trim().toLowerCase();
        var aliasNorm = pbByobNormTagToken(alias);
        for (var p = 0; p < parts.length; p++) {
          if (parts[p] === alias) return true;
          if (pbByobNormTagToken(parts[p]) === aliasNorm) return true;
        }
      }
      /* Any Shopify tag mentioning "variety" (after hyphen/space strip) qualifies — not only bare tokens. */
      if (categoryKey === 'variety_packs') {
        for (var v = 0; v < parts.length; v++) {
          var nv = pbByobNormTagToken(parts[v]);
          if (nv !== '' && nv.indexOf('variety') !== -1) return true;
        }
      }
    }
    if (categoryKey === 'variety_packs') {
      var ttl = pbByobNormTagToken(flavor);
      if (ttl !== '' && ttl.indexOf('variety') !== -1) return true;
    }
    return false;
  }

  function pbByobGridCellForVariant(root, variantId) {
    if (!root || variantId == null || variantId === '') return null;
    var vid = String(variantId);
    return root.querySelector('.pb-byob-product-grid__cell[data-variant-id="' + vid.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"]');
  }

  function ByobBundleController(root) {
    this.root = root;
    this.packFieldset = root.querySelector('.pb-pack-selector');
    this.packSize = readPackSizeFromFieldset(this.packFieldset);
    /**
     * Ordered slot fills. Each entry should align with cart lines:
     * variantId + sellingPlanId (30-day plan on cell) when user selects Subscribe; sellingPlanId ignored for one-time at add-to-cart time.
     * @type {Array<{ variantId: string, sellingPlanId: string, imageSrc: string, title: string }>}
     */
    this.picks = [];
    this.summary = root.querySelector('.pb-byob__selected-summary');
    this.slotsParent = this.summary ? this.summary.querySelector('.pb-byob__selected-slots') : null;
    this.slotEls = this.slotsParent ? Array.prototype.slice.call(this.slotsParent.querySelectorAll(':scope > li')) : [];
    this.buildCta = root.querySelector('[data-pb-byob-build-cta]');
    this.buildCtaMobile = root.querySelector('[data-pb-byob-build-cta-mobile]');
    this.frequencyFieldsetRail = root.querySelector('[data-pb-byob-frequency-rail]');
    this.frequencyFieldsetMobile = root.querySelector('[data-pb-byob-frequency-mobile]');
    /** @deprecated Use frequencyFieldsetRail; kept for any external readers */
    this.frequencyFieldset = this.frequencyFieldsetRail || root.querySelector('.build-your-box__frequency');
    this.frequencyDeliveryNoteEl = root.querySelector('[data-pb-byob-frequency-delivery-note]');
    this.frequencyDeliveryNoteMobile = root.querySelector('[data-pb-byob-frequency-delivery-note-mobile]');
    this.postMixCard = root.querySelector('.pb-byob-post-cta__mix-card');
    this.stickyBuildWrap = root.querySelector('[data-pb-byob-sticky-build]');
    this.stickyBackdrop = root.querySelector('[data-pb-byob-sticky-backdrop]');
    this.stickyBuildToggle = root.querySelector('[data-pb-byob-sticky-toggle]');
    this.stickyBuildCta = root.querySelector('[data-pb-byob-sticky-build-cta]');
    this.stickyBuildPanel = root.querySelector('[data-pb-byob-sticky-panel]');
    this.stickyBuildPanelGrid = root.querySelector('[data-pb-byob-sticky-panel-grid]');
    this.stickyCountCurrentEl = root.querySelector('.build-your-box__sticky-build-count-current');
    this.stickyCountMaxEl = root.querySelector('.build-your-box__sticky-build-count-max');
    this.stickyPanelOpen = false;
    /** Mobile: drawer is transitioning closed (keep sticky chrome visible until done). */
    this._stickyPanelAnimatingClose = false;
    this._stickyPanelCloseTimer = null;
    this._handleStickyPanelClosedTransitionEndBound =
      ByobBundleController.prototype._handleStickyPanelClosedTransitionEnd.bind(this);
    this._summaryInView = true;
    this._summaryObserver = null;
    this._stickyVisibilityFallbackBound = false;
    this._stickyHideTimer = null;
    this._stickyVisible = false;
    this._buildInFlight = false;
    this._wasComplete = false;
    this._manualPricingMap = null;
    this.pickerGridEl = root.querySelector('.build-your-box__picker-grid');
    this.pickerTitleRowEl = root.querySelector('.build-your-box__picker-title-row');
    this.categoryFilters = root.querySelector('.build-your-box__product-category-filters');
    this.categoryFiltersOuter = this.categoryFilters
      ? this.categoryFilters.closest('.build-your-box__product-category-filters-outer')
      : null;
    this._categoryHintRaf = null;
    this._categoryHintRo = null;
    this._categoryHintMo = null;
    this._categoryHintBound = false;
    this._categoryHintLayout = function () {
      var self = this;
      if (typeof window.requestAnimationFrame === 'function') {
        if (self._categoryHintRaf != null) {
          window.cancelAnimationFrame(self._categoryHintRaf);
        }
        self._categoryHintRaf = window.requestAnimationFrame(function () {
          self._categoryHintRaf = null;
          self._syncCategoryScrollHints();
        });
      } else {
        self._syncCategoryScrollHints();
      }
    }.bind(this);
    this._onCategoryHintScroll = function () {
      var self = this;
      if (typeof window.requestAnimationFrame === 'function') {
        if (self._categoryHintRaf != null) {
          window.cancelAnimationFrame(self._categoryHintRaf);
        }
        self._categoryHintRaf = window.requestAnimationFrame(function () {
          self._categoryHintRaf = null;
          self._syncCategoryScrollHints();
        });
      } else {
        self._syncCategoryScrollHints();
      }
    }.bind(this);
    this.railPriceBanner = root.querySelector('[data-pb-byob-rail-price-banner]');
    this.railPriceCurrentEl = this.railPriceBanner
      ? this.railPriceBanner.querySelector('[data-pb-byob-rail-price-current]')
      : null;
    this.railPriceCompareEl = this.railPriceBanner
      ? this.railPriceBanner.querySelector('[data-pb-byob-rail-price-compare]')
      : null;
    this._pickerGridResizeObserver = null;
    this._onPickerTitleScrollChrome = this._onPickerTitleScrollChrome.bind(this);
    this._onDocClick = this._onDocClick.bind(this);
    this._onPackChange = this._onPackChange.bind(this);
    this._onViewport = this._onViewport.bind(this);
    this._onBuildCtaClick = this._onBuildCtaClick.bind(this);
    this._onStickyBuildCtaClick = this._onStickyBuildCtaClick.bind(this);
    this._onStickyToggleKeydown = this._onStickyToggleKeydown.bind(this);
    this._onFrequencyChange = this._onFrequencyChange.bind(this);
    this._onStickyBackdropClick = this._onStickyBackdropClick.bind(this);
    this._onSummaryIntersection = this._onSummaryIntersection.bind(this);
    this._onStickyVisibilityFallback = this._onStickyVisibilityFallback.bind(this);
  }

  ByobBundleController.prototype._onPackChange = function (e) {
    var fs = e.detail && e.detail.fieldset;
    if (!fs || !this.root.contains(fs)) return;
    var next = e.detail.packSize;
    if (!VALID_PACK[next]) return;
    this.packSize = next;
    while (this.picks.length > this.packSize) {
      this.picks.pop();
    }
    this.apply();
  };

  ByobBundleController.prototype._cellForCard = function (card) {
    return card.closest('.pb-byob-product-grid__cell');
  };

  ByobBundleController.prototype._variantFromCard = function (card) {
    var cell = this._cellForCard(card);
    if (!cell) return null;
    if (cell.hidden) return null;
    var id = cell.getAttribute('data-variant-id');
    if (!id) return null;
    var planId = cell.getAttribute('data-pb-byob-30-day-selling-plan-id') || '';
    return {
      variantId: String(id),
      /** Numeric selling plan id from Liquid (30-day allocation); required on cart line when Subscribe & save. */
      sellingPlanId: String(planId).trim(),
      imageSrc: cell.getAttribute('data-pb-flavor-image-src') || '',
      title: cell.getAttribute('data-pb-flavor-title') || '',
    };
  };

  ByobBundleController.prototype._onDocClick = function (e) {
    var root = this.root;
    if (!root.contains(e.target)) return;

    var catBtn = e.target.closest('[data-pb-byob-category]');
    if (catBtn && root.contains(catBtn)) {
      var filterHost = catBtn.closest('.build-your-box__product-category-filters');
      if (filterHost && root.contains(filterHost)) {
        e.preventDefault();
        var catKey = catBtn.getAttribute('data-pb-byob-category') || '';
        this._setActiveCategoryPill(catBtn, catKey, filterHost);
        return;
      }
    }

    var stickyToggle = e.target.closest('[data-pb-byob-sticky-toggle]');
    if (stickyToggle && this.stickyBuildToggle === stickyToggle && this.stickyBuildWrap && !this.stickyBuildWrap.hidden) {
      if (e.target.closest('[data-pb-byob-sticky-build-cta]')) {
        // Let sticky CTA click flow through its dedicated handler.
      } else {
        e.preventDefault();
        this._toggleStickyPanel();
        return;
      }
    }

    var stickyRemove = e.target.closest('[data-pb-byob-sticky-remove]');
    if (stickyRemove && this.stickyBuildPanel && this.stickyBuildPanel.contains(stickyRemove)) {
      var si = parseInt(String(stickyRemove.getAttribute('data-pb-byob-pick-index') || ''), 10);
      if (!isNaN(si) && si >= 0 && si < this.picks.length) {
        e.preventDefault();
        this.picks.splice(si, 1);
        this.apply();
      }
      return;
    }

    var stepBtn = e.target.closest('.pb-bundle-flavor-card__stepper-btn');
    if (stepBtn && !stepBtn.disabled) {
      var card = stepBtn.closest('[data-pb-bundle-flavor-card]');
      if (card && root.contains(card)) {
        var meta = this._variantFromCard(card);
        if (!meta) return;
        e.preventDefault();
        if (stepBtn.classList.contains('pb-bundle-flavor-card__stepper-btn--inc')) {
          if (this.picks.length < this.packSize) {
            this.picks.push({
              variantId: meta.variantId,
              sellingPlanId: meta.sellingPlanId || '',
              imageSrc: meta.imageSrc,
              title: meta.title,
            });
            this.apply();
          }
        } else if (stepBtn.classList.contains('pb-bundle-flavor-card__stepper-btn--dec')) {
          var idx = -1;
          for (var i = this.picks.length - 1; i >= 0; i--) {
            if (this.picks[i].variantId === meta.variantId) {
              idx = i;
              break;
            }
          }
          if (idx !== -1) {
            this.picks.splice(idx, 1);
            this.apply();
          }
        }
      }
      return;
    }

    var removeBtn = e.target.closest('.pb-byob__selected-remove');
    if (removeBtn && this.summary && this.summary.contains(removeBtn)) {
      var li = removeBtn.closest('li.pb-byob__selected-slot');
      if (!li || !this.slotsParent) return;
      var slotIndex = this.slotEls.indexOf(li);
      if (slotIndex < 0 || slotIndex >= this.picks.length) return;
      e.preventDefault();
      this.picks.splice(slotIndex, 1);
      this.apply();
    }
  };

  ByobBundleController.prototype._setActiveCategoryPill = function (clickedBtn, categoryKey, filterHost) {
    var host = filterHost || this.categoryFilters;
    if (!host || !clickedBtn) return;
    var group = host.querySelectorAll('[data-pb-byob-category]');
    for (var i = 0; i < group.length; i++) {
      var b = group[i];
      b.classList.remove('pb-product-selector-tab--active');
      b.setAttribute('aria-pressed', 'false');
    }
    clickedBtn.classList.add('pb-product-selector-tab--active');
    clickedBtn.setAttribute('aria-pressed', 'true');
    this._applyCategoryFilter(categoryKey);
  };

  ByobBundleController.prototype._getActiveCategoryKeyFromDom = function () {
    var host = this.categoryFilters || this.root.querySelector('.build-your-box__product-category-filters');
    if (!host) return 'best_sellers';
    var active = host.querySelector('[data-pb-byob-category].pb-product-selector-tab--active');
    if (active && !active.hidden) return active.getAttribute('data-pb-byob-category') || 'best_sellers';
    var pressed = host.querySelector('[data-pb-byob-category][aria-pressed="true"]');
    if (pressed && !pressed.hidden) return pressed.getAttribute('data-pb-byob-category') || 'best_sellers';
    var visible = host.querySelector('[data-pb-byob-category]:not([hidden])');
    return visible ? visible.getAttribute('data-pb-byob-category') || 'best_sellers' : 'best_sellers';
  };

  /**
   * Toggle `hidden` on product grid cells from `data-pb-byob-tags` vs category aliases.
   * v1: hiding a cell does not remove picks; the user may still have slots for a hidden product until they change filter.
   */
  ByobBundleController.prototype._applyCategoryFilter = function (categoryKey) {
    var k = String(categoryKey || '').trim() || 'best_sellers';
    var cells = this.root.querySelectorAll('.pb-byob-product-grid__cell');
    var i;
    var filterHost = this.categoryFilters || this.root.querySelector('.build-your-box__product-category-filters');
    if (!filterHost) {
      for (i = 0; i < cells.length; i++) {
        cells[i].hidden = false;
      }
      this._onPickerTitleScrollChrome();
      if (this.picks.length === 0) {
        this._syncBuildCta();
      }
      return;
    }
    if (k === 'best_sellers' || k === '') {
      for (i = 0; i < cells.length; i++) {
        var bsCell = cells[i];
        var bsRank = parseInt(String(bsCell.getAttribute('data-pb-byob-bs-rank') || ''), 10);
        var effectiveRank = bsRank;
        if (isNaN(effectiveRank)) {
          effectiveRank = i + 1;
        }
        bsCell.hidden = effectiveRank > PB_BYOB_BEST_SELLERS_TAB_CAP;
      }
    } else {
      for (i = 0; i < cells.length; i++) {
        var cell = cells[i];
        var tagsCsv = cell.getAttribute('data-pb-byob-tags') || '';
        var titleAttr = cell.getAttribute('data-pb-flavor-title') || '';
        cell.hidden = !pbByobCellMatchesCategory(tagsCsv, k, titleAttr);
      }
    }
    this._onPickerTitleScrollChrome();
    if (this.picks.length === 0) {
      this._syncBuildCta();
    }
  };

  /**
   * Hide category pills that have zero grid cells matching `data-pb-byob-tags` (same rules as `_applyCategoryFilter`).
   * Best Sellers is always shown. If the active pill is removed, switch to Best Sellers.
   */
  ByobBundleController.prototype._syncCategoryTabVisibilityFromGrid = function () {
    var host = this.categoryFilters || this.root.querySelector('.build-your-box__product-category-filters');
    if (!host) return;
    var cells = this.root.querySelectorAll('.pb-byob-product-grid__cell');
    var pills = host.querySelectorAll('[data-pb-byob-category]');
    var i;
    for (i = 0; i < pills.length; i++) {
      var btn = pills[i];
      var catKey = (btn.getAttribute('data-pb-byob-category') || '').trim();
      if (!catKey) continue;
      if (catKey === 'best_sellers') {
        btn.hidden = false;
        continue;
      }
      var has = false;
      var c;
      for (c = 0; c < cells.length; c++) {
        if (
          pbByobCellMatchesCategory(
            cells[c].getAttribute('data-pb-byob-tags') || '',
            catKey,
            cells[c].getAttribute('data-pb-flavor-title') || ''
          )
        ) {
          has = true;
          break;
        }
      }
      if (!has) {
        btn.hidden = true;
      } else {
        btn.hidden = false;
      }
    }
    // Never leave a hidden pill as the "active" tab (theme defaults / race); Best Sellers is the fallback.
    var activePill = host.querySelector('[data-pb-byob-category].pb-product-selector-tab--active');
    var pressedBad = false;
    var pressedList = host.querySelectorAll('[data-pb-byob-category][aria-pressed="true"]');
    for (i = 0; i < pressedList.length; i++) {
      if (pressedList[i].hidden) {
        pressedBad = true;
        break;
      }
    }
    var needBest =
      !activePill ||
      activePill.hidden ||
      pressedBad ||
      !host.querySelector('[data-pb-byob-category].pb-product-selector-tab--active:not([hidden])');
    if (needBest) {
      for (i = 0; i < pills.length; i++) {
        pills[i].classList.remove('pb-product-selector-tab--active');
        pills[i].setAttribute('aria-pressed', 'false');
      }
      var best = host.querySelector('[data-pb-byob-category="best_sellers"]');
      if (best) {
        best.classList.add('pb-product-selector-tab--active');
        best.setAttribute('aria-pressed', 'true');
        best.hidden = false;
      }
    }
  };

  /**
   * Each pick counts at most once: first matching category in `PB_BYOB_CATEGORY_BREAKDOWN_ORDER`.
   * Picks with no grid cell or no matching tag are "unmatched" (shown on mix card "Other" only).
   * @returns {{ counts: Object<string, number>, unmatched: number }}
   */
  ByobBundleController.prototype._countsByCategoryFirstMatch = function () {
    var picks = this.picks;
    var root = this.root;
    var catCounts = {};
    var i;
    for (i = 0; i < PB_BYOB_CATEGORY_BREAKDOWN_ORDER.length; i++) {
      catCounts[PB_BYOB_CATEGORY_BREAKDOWN_ORDER[i].key] = 0;
    }
    var matched = 0;
    for (i = 0; i < picks.length; i++) {
      var cell = pbByobGridCellForVariant(root, picks[i].variantId);
      if (!cell) continue;
      var tagsCsv = cell.getAttribute('data-pb-byob-tags') || '';
      var titleAttr = cell.getAttribute('data-pb-flavor-title') || '';
      var o;
      for (o = 0; o < PB_BYOB_CATEGORY_BREAKDOWN_ORDER.length; o++) {
        var catKey = PB_BYOB_CATEGORY_BREAKDOWN_ORDER[o].key;
        if (pbByobCellMatchesCategory(tagsCsv, catKey, titleAttr)) {
          catCounts[catKey]++;
          matched++;
          break;
        }
      }
    }
    return { counts: catCounts, unmatched: picks.length - matched };
  };

  /** Same rule as category pills: hide a mix row when no grid cell matches that category + tags. */
  ByobBundleController.prototype._syncPostCtaMixCategoryVisibility = function () {
    var card = this.postMixCard;
    if (!card) return;
    var cells = this.root.querySelectorAll('.pb-byob-product-grid__cell');
    var rows = card.querySelectorAll('[data-pb-byob-mix-category]');
    var i;
    for (i = 0; i < rows.length; i++) {
      var li = rows[i];
      var catKey = (li.getAttribute('data-pb-byob-mix-category') || '').trim();
      if (catKey === 'other') continue;
      if (!catKey) continue;
      var has = false;
      var c;
      for (c = 0; c < cells.length; c++) {
        if (
          pbByobCellMatchesCategory(
            cells[c].getAttribute('data-pb-byob-tags') || '',
            catKey,
            cells[c].getAttribute('data-pb-flavor-title') || ''
          )
        ) {
          has = true;
          break;
        }
      }
      li.hidden = !has;
    }
  };

  /** Per-category counts + "Other" so displayed totals equal `picks.length` (variety UI). */
  ByobBundleController.prototype._syncPostCtaMixCounts = function () {
    var card = this.postMixCard;
    if (!card) return;
    var res = this._countsByCategoryFirstMatch();
    var counts = res.counts;
    var unmatched = res.unmatched;
    var i;
    for (i = 0; i < PB_BYOB_CATEGORY_BREAKDOWN_ORDER.length; i++) {
      var key = PB_BYOB_CATEGORY_BREAKDOWN_ORDER[i].key;
      var row = card.querySelector('[data-pb-byob-mix-category="' + key + '"]');
      if (!row) continue;
      var span = row.querySelector('[data-pb-byob-mix-count]');
      if (span) span.textContent = String(counts[key] || 0);
    }
    var otherRow = card.querySelector('[data-pb-byob-mix-category="other"]');
    if (otherRow) {
      var ospan = otherRow.querySelector('[data-pb-byob-mix-count]');
      if (ospan) ospan.textContent = String(unmatched);
      otherRow.hidden = unmatched === 0;
    }
  };

  ByobBundleController.prototype._filledInnerHTML = function (pick) {
    var alt = escapeAttr(pick.title || 'Selected product');
    var src = escapeAttr(pick.imageSrc || '');
    return (
      '<div class="pb-byob__selected-slot-clip">' +
      '<div class="pb-byob__selected-slot-media">' +
      (src
        ? '<img class="pb-byob__selected-slot-img" src="' + src + '" alt="' + alt + '" width="128" height="128" loading="lazy">'
        : '') +
      '</div></div>' +
      '<button type="button" class="pb-byob__selected-remove" aria-label="Remove from bundle">' +
      REMOVE_SVG +
      '</button>'
    );
  };

  ByobBundleController.prototype._emptyInnerHTML = function () {
    return (
      '<div class="pb-byob__selected-slot-fill pb-byob__selected-slot-fill--empty">' + EMPTY_PLUS_SVG + '</div>'
    );
  };

  /**
   * Paint progress on the track with a two-stop linear-gradient (reliable vs. % width or `scaleX`
   * inside a pill with `overflow: hidden` in some WebKit builds).
   */
  ByobBundleController.prototype._syncProgressVisual = function () {
    if (!this.summary) return;
    var packSize = this.packSize;
    var n = this.picks.length;
    var frac = packSize > 0 ? n / packSize : 0;
    var pctStr = frac * 100 + '%';
    this.summary.style.setProperty('--pb-byob-selected-progress-f', String(frac));
    this.summary.style.setProperty('--pb-byob-selected-progress-pct', pctStr);
    this.summary.setAttribute('data-pb-byob-progress-pct', String(Math.round(frac * 1000) / 10));

    var track = this.summary.querySelector('.pb-byob__selected-progress-track');
    if (track) {
      if (frac <= 0) {
        track.style.background = PB_BYOB_PROGRESS_TRACK;
      } else if (frac >= 1) {
        track.style.background = PB_BYOB_PROGRESS_FILL;
      } else {
        track.style.background =
          'linear-gradient(to right, ' +
          PB_BYOB_PROGRESS_FILL +
          ' ' +
          pctStr +
          ', ' +
          PB_BYOB_PROGRESS_TRACK +
          ' ' +
          pctStr +
          ')';
      }
    }

    if (pbByobDebugEnabled()) {
      var tw = track ? track.offsetWidth : 0;
      var th = track ? track.offsetHeight : 0;
      console.log('[pb-byob-bundle] progress', {
        picks: n,
        packSize: packSize,
        frac: frac,
        pctStr: pctStr,
        trackOffsetWidth: tw,
        trackOffsetHeight: th,
        hasTrack: !!track,
      });
    }
  };

  /**
   * Edge gradients on `.build-your-box__product-category-filters-outer`: show left / right hints when horizontal overflow exists that way.
   */
  ByobBundleController.prototype._syncCategoryScrollHints = function () {
    var el = this.categoryFilters;
    var outer = this.categoryFiltersOuter;
    if (!el || !outer) return;
    if (typeof window.matchMedia === 'function' && !window.matchMedia('(max-width: 989px)').matches) {
      outer.classList.remove(
        'build-your-box__product-category-filters-outer--hint-left',
        'build-your-box__product-category-filters-outer--hint-right'
      );
      return;
    }
    var maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    if (maxScroll <= 2) {
      outer.classList.remove(
        'build-your-box__product-category-filters-outer--hint-left',
        'build-your-box__product-category-filters-outer--hint-right'
      );
      return;
    }
    var sl = el.scrollLeft;
    var showLeft = sl > 2;
    var showRight = sl < maxScroll - 2;
    outer.classList.toggle('build-your-box__product-category-filters-outer--hint-left', showLeft);
    outer.classList.toggle('build-your-box__product-category-filters-outer--hint-right', showRight);

    if (pbByobDebugEnabled()) {
      console.log('[pb-byob-bundle] category scroll hints', {
        maxScroll: maxScroll,
        scrollLeft: sl,
        showLeft: showLeft,
        showRight: showRight,
      });
    }
  };

  ByobBundleController.prototype._bindCategoryScrollHints = function () {
    if (this._categoryHintBound) return;
    if (!this.categoryFilters || !this.categoryFiltersOuter) return;
    this._categoryHintBound = true;
    var self = this;
    this.categoryFilters.addEventListener('scroll', this._onCategoryHintScroll, { passive: true });
    if (typeof window.ResizeObserver === 'function') {
      this._categoryHintRo = new window.ResizeObserver(this._categoryHintLayout);
      this._categoryHintRo.observe(this.categoryFilters);
    }
    window.addEventListener('resize', this._categoryHintLayout);
    if (typeof window.MutationObserver === 'function') {
      this._categoryHintMo = new window.MutationObserver(this._categoryHintLayout);
      this._categoryHintMo.observe(this.categoryFilters, {
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'class'],
      });
    }
    if (typeof window.matchMedia === 'function') {
      var mq = window.matchMedia('(max-width: 989px)');
      if (mq.addEventListener) mq.addEventListener('change', this._categoryHintLayout);
      else if (mq.addListener) mq.addListener(this._categoryHintLayout);
    }
    try {
      if (typeof document !== 'undefined' && document.fonts && typeof document.fonts.ready !== 'undefined') {
        document.fonts.ready
          .then(function () {
            self._categoryHintLayout();
          })
          .catch(function () {});
      }
    } catch (e) {}
    this._syncCategoryScrollHints();
  };

  /** Toggle hairline + shadow under picker title when the product grid is scrolled. */
  ByobBundleController.prototype._onPickerTitleScrollChrome = function () {
    var grid = this.pickerGridEl;
    var row = this.pickerTitleRowEl;
    if (!grid || !row) return;
    row.classList.toggle('build-your-box__picker-title-row--scrolled', grid.scrollTop > 0);
  };

  /** Matches Liquid default: subscribe radio is checked when nothing selected. Rail and mobile fieldsets stay mirrored. */
  ByobBundleController.prototype._isSubscribeMode = function () {
    var fs = this.frequencyFieldsetRail || this.frequencyFieldsetMobile;
    if (!fs) return true;
    var input = fs.querySelector('input[type="radio"]:checked');
    if (!input) return true;
    return String(input.value) === 'subscribe';
  };

  ByobBundleController.prototype._mirrorFrequencyFieldsets = function (source) {
    if (!source) return;
    var dest = source === this.frequencyFieldsetRail ? this.frequencyFieldsetMobile : this.frequencyFieldsetRail;
    if (!dest) return;
    var ck = source.querySelector('input[type="radio"]:checked');
    if (!ck) return;
    var val = String(ck.value);
    var opts = dest.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < opts.length; i++) {
      if (String(opts[i].value) === val) {
        opts[i].checked = true;
        return;
      }
    }
  };

  /** “Delivered every 30 days” — only relevant when Subscribe is selected. */
  ByobBundleController.prototype._syncFrequencyDeliveryNote = function () {
    var show = this._isSubscribeMode();
    if (this.frequencyDeliveryNoteEl) this.frequencyDeliveryNoteEl.hidden = !show;
    if (this.frequencyDeliveryNoteMobile) this.frequencyDeliveryNoteMobile.hidden = !show;
  };

  /**
   * Toggle the pack-size selector's `data-pb-pack-mode` so the snippet's CSS
   * shows the matching star badge variant (subscribe vs one-time). PB-22.
   */
  ByobBundleController.prototype._syncPackBadgesMode = function () {
    if (!this.packFieldset) return;
    var mode = this._isSubscribeMode() ? 'subscribe' : 'one-time';
    this.packFieldset.setAttribute('data-pb-pack-mode', mode);
  };

  ByobBundleController.prototype._onFrequencyChange = function () {
    this._syncFrequencyDeliveryNote();
    this._syncPackBadgesMode();
  };

  ByobBundleController.prototype._onStickyBackdropClick = function () {
    this._setStickyPanelOpen(false);
  };

  ByobBundleController.prototype._sumPickPricesCents = function () {
    var oneTime = 0;
    var subscribe = 0;
    for (var i = 0; i < this.picks.length; i++) {
      var cell = pbByobGridCellForVariant(this.root, this.picks[i].variantId);
      if (!cell) continue;
      var ot = parseInt(String(cell.getAttribute('data-pb-onetime-price-cents') || '0'), 10);
      var sp = parseInt(String(cell.getAttribute('data-pb-subscribe-price-cents') || '0'), 10);
      if (!isNaN(ot)) oneTime += ot;
      if (!isNaN(sp) && sp > 0) subscribe += sp;
      else if (!isNaN(ot)) subscribe += ot;
    }
    return { oneTime: oneTime, subscribe: subscribe };
  };

  /** First visible, in-stock grid cell — anchor unit when slots are unfilled. */
  ByobBundleController.prototype._firstVisibleProductGridCell = function () {
    var cells = this.root.querySelectorAll('.pb-byob-product-grid__cell');
    for (var i = 0; i < cells.length; i++) {
      var el = cells[i];
      if (el.hidden) continue;
      if (!el.getAttribute('data-variant-id')) continue;
      return el;
    }
    return null;
  };

  /** Subscribe-allocation unit price from a cell (falls back to one-time cents then anchor). */
  ByobBundleController.prototype._subscribeUnitFromCell = function (cell, anchorFallbackOt) {
    var fb =
      typeof anchorFallbackOt === 'number' && !isNaN(anchorFallbackOt) && anchorFallbackOt > 0
        ? anchorFallbackOt
        : 0;
    if (!cell) return fb;
    var sp = parseInt(String(cell.getAttribute('data-pb-subscribe-price-cents') || '0'), 10);
    var ot = parseInt(String(cell.getAttribute('data-pb-onetime-price-cents') || '0'), 10);
    if (isNaN(ot)) ot = 0;
    if (!isNaN(sp) && sp > 0) return sp;
    if (ot > 0) return ot;
    return fb;
  };

  /**
   * Catalogue one-time subtotal for the frequency row: each of `packSize` slots uses the picked
   * variant’s `data-pb-onetime-price-cents` when that slot is filled; unfilled slots use the first
   * visible grid unit (same UX as cart when the bundle is full).
   */
  ByobBundleController.prototype._bundleOneTimeCatalogSubtotalCents = function () {
    var packSize = this.packSize;
    var anchorCell = this._firstVisibleProductGridCell();
    var anchorOt = 0;
    if (anchorCell) {
      var ao = parseInt(String(anchorCell.getAttribute('data-pb-onetime-price-cents') || '0'), 10);
      if (!isNaN(ao) && ao > 0) anchorOt = ao;
    }
    var total = 0;
    var picks = this.picks;
    var i;
    for (i = 0; i < packSize; i++) {
      var slotOt = anchorOt;
      if (i < picks.length && picks[i] && picks[i].variantId != null && String(picks[i].variantId).trim() !== '') {
        var c = pbByobGridCellForVariant(this.root, picks[i].variantId);
        if (c) {
          var ot = parseInt(String(c.getAttribute('data-pb-onetime-price-cents') || '0'), 10);
          if (!isNaN(ot) && ot > 0) slotOt = ot;
        }
      }
      total += slotOt;
    }
    return total;
  };

  /**
   * Manual one-time % display: Shopify allocates %-off **per catalogue unit** (each bundle slot /
   * line-unit), truncating discount cents with floor—not round(bundle × pct). Example: two × $39.99 @ 10%
   * → floor(3999×10/100)×2 = $7.98 saved, not round($79.98×10%) = $8.00.
   */
  ByobBundleController.prototype._oneTimeManualDiscountStrikeSaleCents = function (discountPct) {
    var packSize = this.packSize;
    var anchorCell = this._firstVisibleProductGridCell();
    var anchorOt = 0;
    if (anchorCell) {
      var ao = parseInt(String(anchorCell.getAttribute('data-pb-onetime-price-cents') || '0'), 10);
      if (!isNaN(ao) && ao > 0) anchorOt = ao;
    }
    var strike = 0;
    var discountSum = 0;
    var picks = this.picks;
    var i;
    for (i = 0; i < packSize; i++) {
      var slotOt = anchorOt;
      if (i < picks.length && picks[i] && picks[i].variantId != null && String(picks[i].variantId).trim() !== '') {
        var c = pbByobGridCellForVariant(this.root, picks[i].variantId);
        if (c) {
          var ot = parseInt(String(c.getAttribute('data-pb-onetime-price-cents') || '0'), 10);
          if (!isNaN(ot) && ot > 0) slotOt = ot;
        }
      }
      strike += slotOt;
      discountSum += Math.floor((slotOt * discountPct) / 100);
    }
    return { strike: strike, sale: strike - discountSum };
  };

  /**
   * Catalogue subscribe subtotal for the frequency row — per-slot picks with anchor fill, mirroring `_bundleOneTimeCatalogSubtotalCents`.
   */
  ByobBundleController.prototype._bundleSubscribeCatalogSubtotalCents = function () {
    var packSize = this.packSize;
    var anchorCell = this._firstVisibleProductGridCell();
    var anchorOt = 0;
    if (anchorCell) {
      var ao = parseInt(String(anchorCell.getAttribute('data-pb-onetime-price-cents') || '0'), 10);
      if (!isNaN(ao) && ao > 0) anchorOt = ao;
    }
    var anchorSub = this._subscribeUnitFromCell(anchorCell, anchorOt);
    var total = 0;
    var picks = this.picks;
    var i;
    for (i = 0; i < packSize; i++) {
      var slotSub = anchorSub;
      if (i < picks.length && picks[i] && picks[i].variantId != null && String(picks[i].variantId).trim() !== '') {
        var c = pbByobGridCellForVariant(this.root, picks[i].variantId);
        slotSub = this._subscribeUnitFromCell(c, anchorSub);
      }
      total += slotSub;
    }
    return total;
  };

  /**
   * Per-pack one-time display discount from `data-pb-byob-onetime-discount-pack-{pack}` (0–99).
   * 0 = off (falls back to compare-at strike logic only). Unknown pack → 0.
   */
  ByobBundleController.prototype._onetimeDiscountPercentForPack = function (pack) {
    var n = parseInt(String(pack || ''), 10);
    if (isNaN(n)) return 0;
    var raw = parseInt(String(this.root.getAttribute('data-pb-byob-onetime-discount-pack-' + String(n)) || ''), 10);
    if (isNaN(raw) || raw < 1 || raw > 99) return 0;
    return raw;
  };

  /**
   * Subscribe / One-Time frequency row totals:
   * - One-time catalogue base = sum over `packSize` slots (picked variant or anchor placeholder).
   * - Manual %-off: matches Shopify-style **per-slot** discount sum — Σ floor((slot cents × pct) / 100), not round(total × pct).
   */
  ByobBundleController.prototype._totalsForFrequencyLabels = function () {
    var packSize = this.packSize;
    var cell = this._firstVisibleProductGridCell();
    if (!cell) {
      return { oneTime: 0, subscribe: 0, oneTimeCompare: 0, subscribeCompare: 0 };
    }
    var ot = parseInt(String(cell.getAttribute('data-pb-onetime-price-cents') || '0'), 10);
    var sp = parseInt(String(cell.getAttribute('data-pb-subscribe-price-cents') || '0'), 10);
    if (isNaN(ot)) ot = 0;
    if (isNaN(sp)) sp = 0;
    var subUnit = !isNaN(sp) && sp > 0 ? sp : ot;

    var otCmp = parseInt(String(cell.getAttribute('data-pb-onetime-compare-price-cents') || ''), 10);
    var spCmp = parseInt(String(cell.getAttribute('data-pb-subscribe-compare-price-cents') || ''), 10);
    if (isNaN(otCmp)) otCmp = 0;
    if (isNaN(spCmp)) spCmp = 0;

    var oneTimeBase = this._bundleOneTimeCatalogSubtotalCents();
    var discountPct = this._onetimeDiscountPercentForPack(packSize);

    /** Display one-time bundle total (featured price in UI). */
    var oneTimeDisplay;
    /** Struck-through one-time bundle total — manual discount strikes full base OR Shopify compare-at when no manual discount. */
    var oneTimeStrikeCents;
    if (discountPct > 0) {
      var mm = this._oneTimeManualDiscountStrikeSaleCents(discountPct);
      oneTimeStrikeCents = mm.strike;
      oneTimeDisplay = mm.sale;
      if (oneTimeStrikeCents <= oneTimeDisplay) {
        oneTimeStrikeCents = 0;
      }
    } else {
      oneTimeDisplay = oneTimeBase;
      oneTimeStrikeCents = otCmp > ot ? otCmp * packSize : 0;
    }

    var subscribeBundle = this._bundleSubscribeCatalogSubtotalCents();
    var subDiscPct = this._subscribeDiscountPercentForPack(packSize);
    var anchorUnit = this._frequencyAnchorUnitCents();
    var anchorSubtotal = anchorUnit > 0 ? anchorUnit * packSize : 0;
    if (subDiscPct > 0 && anchorSubtotal > 0) {
      var saveOffAnchor = Math.round((anchorSubtotal * subDiscPct) / 100);
      subscribeBundle = anchorSubtotal - saveOffAnchor;
    }

    var subscribeCompare = 0;
    if (sp > 0 && spCmp > sp) {
      subscribeCompare = spCmp * packSize;
    } else if (otCmp > subUnit) {
      subscribeCompare = otCmp * packSize;
    }
    if (subDiscPct > 0 && anchorSubtotal > 0) {
      subscribeCompare = Math.max(subscribeCompare, anchorSubtotal);
    }

    return {
      oneTime: oneTimeDisplay,
      subscribe: subscribeBundle,
      oneTimeCompare: oneTimeStrikeCents,
      subscribeCompare: subscribeCompare,
    };
  };

  ByobBundleController.prototype._frequencyAnchorUnitCents = function () {
    var raw = this.root.getAttribute('data-pb-byob-frequency-anchor-unit-cents');
    var n = parseInt(String(raw || ''), 10);
    if (isNaN(n) || n < 0) return 0;
    return n;
  };

  /** @returns {Record<string, object>} */
  ByobBundleController.prototype._getManualPricingMap = function () {
    if (this._manualPricingMap != null && typeof this._manualPricingMap === 'object') {
      return this._manualPricingMap;
    }
    var script = this.root.querySelector('script[type="application/json"][data-pb-byob-pack-pricing-json]');
    if (!script || !String(script.textContent || '').trim()) {
      this._manualPricingMap = {};
      return this._manualPricingMap;
    }
    try {
      var parsed = JSON.parse(script.textContent);
      this._manualPricingMap =
        parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
      this._manualPricingMap = {};
    }
    return this._manualPricingMap;
  };

  /** @returns {object | null} */
  ByobBundleController.prototype._manualPricingRow = function (packSize) {
    var row = this._getManualPricingMap()[String(packSize)];
    return row && typeof row === 'object' ? row : null;
  };

  /** True when merchants filled any Step 3 frequency display field — uses manual strings verbatim. */
  ByobBundleController.prototype._manualFrequencyDisplayActive = function (packSize) {
    function nz(x) {
      return String(x == null ? '' : x).trim();
    }
    var row = this._manualPricingRow(packSize);
    if (!row) return false;
    return (
      nz(row.frequency_subscribe_price) !== '' ||
      nz(row.frequency_subscribe_save) !== '' ||
      nz(row.frequency_subscribe_compare) !== '' ||
      nz(row.frequency_onetime_price) !== '' ||
      nz(row.frequency_onetime_compare) !== '' ||
      nz(row.frequency_onetime_save) !== ''
    );
  };

  /**
   * Per-pack Subscribe pink Save + anchor sale math — `data-pb-byob-subscribe-discount-pack-{pack}` as text (decimals OK, e.g. 26.25).
   */
  ByobBundleController.prototype._subscribeDiscountPercentForPack = function (pack) {
    var n = parseInt(String(pack || ''), 10);
    if (isNaN(n)) return 0;
    var rawStr = String(this.root.getAttribute('data-pb-byob-subscribe-discount-pack-' + String(n)) || '').trim();
    if (rawStr === '') return 0;
    var normalized = rawStr.replace(',', '.');
    var pct = parseFloat(normalized);
    if (!isFinite(pct) || pct <= 0 || pct > 100) return 0;
    return pct;
  };

  /**
   * Step 3 Subscribe row pink Save $ = round(anchorUnitCents × pack × subscribe pack percent ÷ 100).
   */
  ByobBundleController.prototype._syncSubscribePackSavings = function () {
    var pack = this.packSize;
    if (!VALID_PACK[pack]) pack = 4;
    var row = this._manualPricingRow(pack);
    var manual = this._manualFrequencyDisplayActive(pack);
    var els = this.root.querySelectorAll('[data-pb-byob-subscribe-pack-savings]');
    var i;
    if (manual && row) {
      var chip = String(row.frequency_subscribe_save == null ? '' : row.frequency_subscribe_save).trim();
      for (i = 0; i < els.length; i++) els[i].textContent = chip;
      return;
    }
    var unit = this._frequencyAnchorUnitCents();
    var pct = this._subscribeDiscountPercentForPack(pack);
    var out = '';
    if (unit > 0 && pct > 0) {
      var anchorSubtotal = unit * pack;
      var saveCents = Math.round((anchorSubtotal * pct) / 100);
      if (saveCents > 0) {
        out = 'Save ' + pbByobFormatMoneyCents(saveCents);
      }
    }
    for (i = 0; i < els.length; i++) els[i].textContent = out;
  };

  /**
   * Step 3 One-time row: Save $ = compare − sale from `_totalsForFrequencyLabels`
   * (per-slot catalogue subtotal + explicit discount rounding).
   */
  ByobBundleController.prototype._syncOnetimePackSavings = function () {
    var pack = this.packSize;
    if (!VALID_PACK[pack]) pack = 4;
    var row = this._manualPricingRow(pack);
    var manual = this._manualFrequencyDisplayActive(pack);
    var els = this.root.querySelectorAll('[data-pb-byob-onetime-pack-savings]');
    var i;
    if (manual && row) {
      var chip = String(row.frequency_onetime_save == null ? '' : row.frequency_onetime_save).trim();
      for (i = 0; i < els.length; i++) els[i].textContent = chip;
      return;
    }
    var totals = this._totalsForFrequencyLabels();
    var cmp = totals.oneTimeCompare != null ? totals.oneTimeCompare : 0;
    var sale = totals.oneTime != null ? totals.oneTime : 0;
    var saveCents = cmp > sale ? cmp - sale : 0;
    var out = '';
    if (saveCents > 0 && sale > 0) {
      out = 'Save ' + pbByobFormatMoneyCents(saveCents);
    }
    for (i = 0; i < els.length; i++) els[i].textContent = out;
  };

  ByobBundleController.prototype._syncPriceLabels = function () {
    var packSize = this.packSize;
    if (!VALID_PACK[packSize]) packSize = 4;
    var rowManual = this._manualPricingRow(packSize);
    var manualFreq = this._manualFrequencyDisplayActive(packSize);
    var subEls = this.root.querySelectorAll('[data-pb-byob-price-role="subscribe"]');
    var otEls = this.root.querySelectorAll('[data-pb-byob-price-role="one-time"]');
    var i;
    if (manualFreq && rowManual) {
      var subSaleStr = String(rowManual.frequency_subscribe_price == null ? '' : rowManual.frequency_subscribe_price).trim();
      var subStrikeStrM = String(rowManual.frequency_subscribe_compare == null ? '' : rowManual.frequency_subscribe_compare).trim();
      for (i = 0; i < subEls.length; i++) {
        var subHostM = subEls[i];
        var subCmpM = subHostM.querySelector('[data-pb-byob-subscribe-display-compare]');
        var subCurM = subHostM.querySelector('[data-pb-byob-subscribe-display-current]');
        if (subCmpM && subCurM) {
          subCmpM.textContent = subStrikeStrM;
          subCurM.textContent = subSaleStr !== '' ? subSaleStr : '—';
        } else {
          var subFallbackM = subSaleStr !== '' ? subSaleStr : '—';
          subHostM.textContent = subFallbackM;
        }
      }
      var otSaleStr = String(rowManual.frequency_onetime_price == null ? '' : rowManual.frequency_onetime_price).trim();
      var otStrikeStr = String(rowManual.frequency_onetime_compare == null ? '' : rowManual.frequency_onetime_compare).trim();
      for (i = 0; i < otEls.length; i++) {
        var otHostM = otEls[i];
        var cmpSpanM = otHostM.querySelector('[data-pb-byob-onetime-display-compare]');
        var curSpanM = otHostM.querySelector('[data-pb-byob-onetime-display-current]');
        if (cmpSpanM && curSpanM) {
          cmpSpanM.textContent = otStrikeStr;
          curSpanM.textContent = otSaleStr !== '' ? otSaleStr : '—';
        } else {
          otHostM.textContent = otSaleStr !== '' ? otSaleStr : '—';
        }
      }
      return;
    }
    var totals = this._totalsForFrequencyLabels();
    var subStr = totals.subscribe > 0 ? pbByobFormatMoneyCents(totals.subscribe) : '—';
    var subStrikeAuto =
      totals.subscribeCompare != null &&
      totals.subscribeCompare !== undefined &&
      totals.subscribeCompare > totals.subscribe
        ? totals.subscribeCompare
        : 0;
    for (i = 0; i < subEls.length; i++) {
      var subHost = subEls[i];
      var subCmp = subHost.querySelector('[data-pb-byob-subscribe-display-compare]');
      var subCur = subHost.querySelector('[data-pb-byob-subscribe-display-current]');
      if (subCmp && subCur) {
        subCmp.textContent = subStrikeAuto > 0 ? pbByobFormatMoneyCents(subStrikeAuto) : '';
        subCur.textContent = totals.subscribe > 0 ? pbByobFormatMoneyCents(totals.subscribe) : '—';
      } else {
        subHost.textContent = subStr;
      }
    }
    var otSale = totals.oneTime;
    var otStrike =
      totals.oneTimeCompare != null &&
      totals.oneTimeCompare !== undefined &&
      totals.oneTimeCompare > otSale
        ? totals.oneTimeCompare
        : 0;
    for (i = 0; i < otEls.length; i++) {
      var otHost = otEls[i];
      var cmpSpan = otHost.querySelector('[data-pb-byob-onetime-display-compare]');
      var curSpan = otHost.querySelector('[data-pb-byob-onetime-display-current]');
      if (cmpSpan && curSpan) {
        cmpSpan.textContent = otStrike > 0 ? pbByobFormatMoneyCents(otStrike) : '';
        curSpan.textContent = otSale > 0 ? pbByobFormatMoneyCents(otSale) : '—';
      } else {
        otHost.textContent = otSale > 0 ? pbByobFormatMoneyCents(otSale) : '—';
      }
    }
  };

  /**
   * Mobile (<990px CSS): headline price in `.build-your-box__rail-price-summary` mirrors the
   * same anchor × pack totals as `_syncPriceLabels`; optional compare-at or manual one-time
   * discount strike shown first.
   */
  ByobBundleController.prototype._syncRailMobilePriceBanner = function () {
    var wrap = this.railPriceBanner;
    var curEl = this.railPriceCurrentEl;
    var cmpEl = this.railPriceCompareEl;
    if (!wrap || !curEl) return;

    var packSizeRail = this.packSize;
    if (!VALID_PACK[packSizeRail]) packSizeRail = 4;
    var rowRail = this._manualPricingRow(packSizeRail);
    var modeSub = this._isSubscribeMode();
    var manualCurRail =
      rowRail && this._manualFrequencyDisplayActive(packSizeRail)
        ? modeSub
          ? String(rowRail.frequency_subscribe_price == null ? '' : rowRail.frequency_subscribe_price).trim()
          : String(rowRail.frequency_onetime_price == null ? '' : rowRail.frequency_onetime_price).trim()
        : '';
    if (manualCurRail !== '' && rowRail) {
      var curText = manualCurRail;
      var cmpText = '';
      if (!modeSub) {
        cmpText = String(rowRail.frequency_onetime_compare == null ? '' : rowRail.frequency_onetime_compare).trim();
      } else {
        cmpText = String(rowRail.frequency_subscribe_compare == null ? '' : rowRail.frequency_subscribe_compare).trim();
      }
      curEl.textContent = curText;
      if (cmpEl) cmpEl.textContent = cmpText;
      wrap.setAttribute('data-pb-byob-rail-price-state', 'on');
      return;
    }

    var totals = this._totalsForFrequencyLabels();
    var curCents = modeSub ? totals.subscribe : totals.oneTime;
    var cmpCents = 0;
    if (!modeSub) {
      cmpCents =
        totals.oneTimeCompare != null &&
        totals.oneTimeCompare !== undefined &&
        totals.oneTimeCompare > totals.oneTime
          ? totals.oneTimeCompare
          : 0;
    } else {
      cmpCents =
        totals.subscribeCompare != null && totals.subscribeCompare > totals.subscribe
          ? totals.subscribeCompare
          : 0;
    }

    if (curCents <= 0) {
      wrap.setAttribute('data-pb-byob-rail-price-state', 'off');
      curEl.textContent = '';
      if (cmpEl) cmpEl.textContent = '';
      return;
    }

    curEl.textContent = pbByobFormatMoneyCents(curCents);
    if (cmpEl) {
      if (cmpCents > curCents) {
        cmpEl.textContent = pbByobFormatMoneyCents(cmpCents);
      } else {
        cmpEl.textContent = '';
      }
    }
    wrap.setAttribute('data-pb-byob-rail-price-state', 'on');
  };

  ByobBundleController.prototype._setBuildButtonState = function (btn, label, complete) {
    if (!btn) return;
    var labelEl = btn.querySelector('.build-your-box__build-cta-label');
    if (labelEl) labelEl.textContent = label;
    if (complete) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
    } else {
      btn.setAttribute('disabled', '');
      btn.setAttribute('aria-disabled', 'true');
    }
  };

  ByobBundleController.prototype._setStickyBuildButtonState = function (label, complete) {
    var btn = this.stickyBuildCta;
    if (!btn) return;
    var labelEl = btn.querySelector('.build-your-box__build-cta-label');
    if (labelEl) labelEl.textContent = label;
    if (complete) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
    } else {
      btn.removeAttribute('disabled');
      btn.setAttribute('aria-disabled', 'false');
    }
  };

  ByobBundleController.prototype._scrollToBuilderStart = function () {
    var target =
      this.root.querySelector('.build-your-box__controls-rail-top') ||
      this.root.querySelector('.build-your-box__picker-panel') ||
      this.root;
    if (!target || !target.scrollIntoView) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  ByobBundleController.prototype._syncStickyBuildCounts = function (currentCount, packSize) {
    if (this.stickyCountCurrentEl) this.stickyCountCurrentEl.textContent = String(currentCount);
    if (this.stickyCountMaxEl) this.stickyCountMaxEl.textContent = String(packSize);
  };

  ByobBundleController.prototype._stickyPanelItemWithRemoveHtml = function (pick, index) {
    return (
      '<li class="build-your-box__sticky-build-panel-item">' +
      '<div class="build-your-box__sticky-build-panel-tile">' +
      '<div class="build-your-box__sticky-build-panel-thumb">' +
      (pick.imageSrc
        ? '<img src="' +
          escapeAttr(pick.imageSrc || '') +
          '" alt="' +
          escapeAttr(pick.title || 'Selected product') +
          '" width="128" height="128" loading="lazy">'
        : '') +
      '</div>' +
      '<button type="button" class="build-your-box__sticky-build-remove" data-pb-byob-sticky-remove data-pb-byob-pick-index="' +
      String(index) +
      '" aria-label="Remove from bundle">' +
      REMOVE_SVG +
      '</button>' +
      '</div>' +
      '</li>'
    );
  };

  ByobBundleController.prototype._stickyPanelPlaceholderHtml = function () {
    return (
      '<li class="build-your-box__sticky-build-panel-item">' +
      '<div class="build-your-box__sticky-build-panel-tile build-your-box__sticky-build-panel-tile--empty">' +
      '<span class="build-your-box__sticky-build-panel-plus" aria-hidden="true">' +
      EMPTY_PLUS_SVG +
      '</span>' +
      '</div>' +
      '</li>'
    );
  };

  ByobBundleController.prototype._syncStickyPanelContent = function () {
    var grid = this.stickyBuildPanelGrid;
    if (!grid) return;
    var cap = Math.min(this.packSize, MAX_PACK);
    var html = '';
    var i;
    for (i = 0; i < cap; i++) {
      if (i < this.picks.length) html += this._stickyPanelItemWithRemoveHtml(this.picks[i], i);
      else html += this._stickyPanelPlaceholderHtml();
    }
    grid.innerHTML = html;
  };

  ByobBundleController.prototype._handleStickyPanelClosedTransitionEnd = function (e) {
    var panel = this.stickyBuildPanel;
    if (!panel) return;
    var ev = e || {};
    if (ev.target && ev.target !== panel) return;
    /* Timer fallback omits propertyName — real drawer events use `transform` only. */
    if (ev.propertyName) {
      var p = String(ev.propertyName).replace(/^-webkit-/i, '');
      if (String(p || '').toLowerCase() !== 'transform') return;
    }

    panel.removeEventListener('transitionend', this._handleStickyPanelClosedTransitionEndBound);
    if (this._stickyPanelCloseTimer) {
      clearTimeout(this._stickyPanelCloseTimer);
      this._stickyPanelCloseTimer = null;
    }

    if (!this.stickyPanelOpen) {
      panel.setAttribute('hidden', '');
      panel.setAttribute('aria-hidden', 'true');
    }
    this._stickyPanelAnimatingClose = false;
    this._onStickyVisibilityFallback();
  };

  ByobBundleController.prototype._setStickyPanelOpen = function (on) {
    var open = !!on;
    var mobile = this._isPbByobMobileViewport();
    var panel = this.stickyBuildPanel;
    var wrap = this.stickyBuildWrap;

    if (mobile && panel) {
      if (this._stickyPanelCloseTimer) {
        clearTimeout(this._stickyPanelCloseTimer);
        this._stickyPanelCloseTimer = null;
      }
      panel.removeEventListener('transitionend', this._handleStickyPanelClosedTransitionEndBound);
    }

    this.stickyPanelOpen = open;

    if (this.stickyBuildToggle) {
      this.stickyBuildToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    if (mobile && panel) {
      if (open) {
        this._stickyPanelAnimatingClose = false;
        panel.removeAttribute('hidden');
        panel.setAttribute('aria-hidden', 'false');
        if (wrap) {
          wrap.classList.remove('is-panel-open');
          var selfOpen = this;
          if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(function () {
              window.requestAnimationFrame(function () {
                if (selfOpen.stickyPanelOpen && selfOpen.stickyBuildWrap) {
                  selfOpen.stickyBuildWrap.classList.add('is-panel-open');
                }
              });
            });
          } else {
            wrap.classList.add('is-panel-open');
          }
        }
      } else {
        var wasOpenUi = wrap && wrap.classList.contains('is-panel-open');
        this._stickyPanelAnimatingClose = wasOpenUi;
        if (wrap) wrap.classList.remove('is-panel-open');
        if (!wasOpenUi) {
          panel.setAttribute('hidden', '');
          panel.setAttribute('aria-hidden', 'true');
          this._stickyPanelAnimatingClose = false;
        } else {
          panel.setAttribute('aria-hidden', 'true');
          panel.addEventListener('transitionend', this._handleStickyPanelClosedTransitionEndBound);
          var selfClose = this;
          this._stickyPanelCloseTimer = setTimeout(function () {
            selfClose._handleStickyPanelClosedTransitionEnd.call(selfClose, { target: panel });
          }, 420);
        }
      }
    } else {
      this._stickyPanelAnimatingClose = false;
      if (panel) {
        panel.hidden = !open;
        panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      }
      if (wrap) wrap.classList.toggle('is-panel-open', open);
    }

    if (this.stickyBackdrop) {
      if (!open) {
        this.stickyBackdrop.hidden = true;
        this.stickyBackdrop.setAttribute('aria-hidden', 'true');
      } else if (mobile) {
        this.stickyBackdrop.hidden = false;
        this.stickyBackdrop.setAttribute('aria-hidden', 'false');
      } else {
        this.stickyBackdrop.hidden = true;
        this.stickyBackdrop.setAttribute('aria-hidden', 'true');
      }
    }
  };

  ByobBundleController.prototype._toggleStickyPanel = function () {
    if (!this.stickyBuildPanel) return;
    this._setStickyPanelOpen(!this.stickyPanelOpen);
  };

  ByobBundleController.prototype._syncStickyBuildVisibility = function () {
    var wrap = this.stickyBuildWrap;
    if (!wrap) return;
    if (!this.summary) {
      wrap.hidden = true;
      wrap.classList.remove('is-anim-ready', 'is-hidden-top', 'is-hidden-bottom');
      wrap.setAttribute('aria-hidden', 'true');
      this._setStickyPanelOpen(false);
      return;
    }
    this._summaryInView = this._computeSummaryInViewFromRect();
    var show = !this._summaryInView;
    if (
      this._isPbByobMobileViewport() &&
      (this.stickyPanelOpen || this._stickyPanelAnimatingClose)
    ) {
      show = true;
    }
    this._setStickyBuildVisible(show, true);
  };

  ByobBundleController.prototype._stickyHiddenClass = function () {
    var host = this.root.closest('.build-your-box');
    var pos = host ? String(host.getAttribute('data-pb-byob-sticky-cta-position') || 'top') : 'top';
    return pos === 'bottom' ? 'is-hidden-bottom' : 'is-hidden-top';
  };

  ByobBundleController.prototype._setStickyBuildVisible = function (show, animated) {
    var wrap = this.stickyBuildWrap;
    if (!wrap) return;
    var hiddenClass = this._stickyHiddenClass();
    if (this._stickyHideTimer) {
      clearTimeout(this._stickyHideTimer);
      this._stickyHideTimer = null;
    }

    if (!animated) {
      wrap.classList.remove('is-anim-ready', 'is-hidden-top', 'is-hidden-bottom');
      if (!show) wrap.classList.add(hiddenClass);
      wrap.hidden = !show;
      this._stickyVisible = !!show;
      wrap.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (!show) this._setStickyPanelOpen(false);
      return;
    }

    wrap.classList.add('is-anim-ready');
    if (show) {
      wrap.hidden = false;
      wrap.setAttribute('aria-hidden', 'false');
      if (!this._stickyVisible) {
        wrap.classList.add(hiddenClass);
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
              wrap.classList.remove(hiddenClass);
            });
          });
        } else {
          setTimeout(function () {
            wrap.classList.remove(hiddenClass);
          }, 0);
        }
      } else {
        wrap.classList.remove(hiddenClass);
      }
      this._stickyVisible = true;
      return;
    }

    wrap.setAttribute('aria-hidden', 'true');
    wrap.classList.add(hiddenClass);
    this._stickyVisible = false;
    this._setStickyPanelOpen(false);
    this._stickyHideTimer = setTimeout(function () {
      if (!wrap.classList.contains(hiddenClass)) return;
      wrap.hidden = true;
    }, 260);
  };

  ByobBundleController.prototype._computeSummaryInViewFromRect = function () {
    if (!this.summary || !this.summary.getBoundingClientRect) return true;
    var rect = this.summary.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.bottom > 0 && rect.top < vh;
  };

  ByobBundleController.prototype._onSummaryIntersection = function (entries) {
    if (!entries || !entries.length) return;
    this._summaryInView = !!entries[0].isIntersecting;
    this._syncStickyBuildVisibility();
  };

  ByobBundleController.prototype._onStickyVisibilityFallback = function () {
    this._summaryInView = this._computeSummaryInViewFromRect();
    this._syncStickyBuildVisibility();
  };

  ByobBundleController.prototype._bindStickySummaryObserver = function () {
    if (!this.stickyBuildWrap || !this.summary) return;
    if (typeof window.IntersectionObserver === 'function') {
      if (!this._summaryObserver) {
        this._summaryObserver = new window.IntersectionObserver(this._onSummaryIntersection, {
          root: null,
          threshold: 0.01,
        });
        this._summaryObserver.observe(this.summary);
      }
    }
    /* Keep a deterministic scroll/resize fallback active even when IO exists. */
    if (this._stickyVisibilityFallbackBound) return;
    this._stickyVisibilityFallbackBound = true;
    window.addEventListener('scroll', this._onStickyVisibilityFallback, { passive: true });
    window.addEventListener('resize', this._onStickyVisibilityFallback);
  };

  ByobBundleController.prototype._onStickyBuildCtaClick = function (e) {
    var btn = this.stickyBuildCta;
    if (!btn || e.currentTarget !== btn) return;
    e.preventDefault();
    var complete = this.packSize > 0 && this.picks.length >= this.packSize;
    if (complete) {
      if (this.buildCta) this.buildCta.click();
      return;
    }
    this._scrollToBuilderStart();
  };

  ByobBundleController.prototype._onStickyToggleKeydown = function (e) {
    var t = e.target;
    if (!this.stickyBuildToggle || t !== this.stickyBuildToggle) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._toggleStickyPanel();
    }
  };

  /**
   * @returns {{ items: Array<Object>, error: string } | { items: Array, error: null }}
   */
  ByobBundleController.prototype._buildCartItemsFromPicks = function () {
    var packSize = this.packSize;
    var picks = this.picks;
    if (packSize <= 0 || picks.length !== packSize) {
      return { items: [], error: 'Select all products for your box before building.' };
    }
    var subscribe = this._isSubscribeMode();
    var agg = {};
    for (var i = 0; i < picks.length; i++) {
      var p = picks[i];
      var vid = String(p.variantId || '').trim();
      var idNum = parseInt(vid, 10);
      if (!vid || isNaN(idNum) || idNum < 1) {
        return { items: [], error: 'Something went wrong with a product selection. Refresh and try again.' };
      }
      if (subscribe) {
        var sidRaw = String(p.sellingPlanId || '').trim();
        var planNum = parseInt(sidRaw, 10);
        if (!sidRaw || isNaN(planNum) || planNum < 1) {
          return {
            items: [],
            error: 'Subscribe & Save requires a selling plan for each item. Refresh or choose One time.',
          };
        }
        var key = vid + ':' + sidRaw;
        if (!agg[key]) {
          agg[key] = { id: idNum, quantity: 0, selling_plan: planNum };
        }
        agg[key].quantity += 1;
      } else {
        if (!agg[vid]) {
          agg[vid] = { id: idNum, quantity: 0 };
        }
        agg[vid].quantity += 1;
      }
    }
    var items = [];
    for (var k in agg) {
      if (!Object.prototype.hasOwnProperty.call(agg, k)) continue;
      items.push(agg[k]);
    }
    return { items: items, error: null };
  };

  ByobBundleController.prototype._subscribeDiscountCodeForPack = function () {
    var packSize = this.packSize;
    var n = parseInt(String(packSize || ''), 10);
    if (!VALID_PACK[n]) return null;
    var raw = this.root.getAttribute('data-pb-byob-cart-discount-' + String(n));
    var trimmed = raw != null ? String(raw).trim() : '';
    if (trimmed !== '') return trimmed;
    return 'BYOB' + String(n) + '-' + PB_BYOB_DISCOUNT_CODE_SUFFIX;
  };

  ByobBundleController.prototype._byobNotifyUser = function (message) {
    var msg = String(message || 'Something went wrong.');
    if (typeof window.alert === 'function') window.alert(msg);
  };

  /** Same breakpoint as `build-your-box` mobile/sticky UI (≤989px). */
  ByobBundleController.prototype._isPbByobMobileViewport = function () {
    return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 989px)').matches;
  };

  /**
   * Mobile: dismiss the builder sheet/backdrop (if open), then open the cart drawer after the
   * current stack so `cart-update` subscribers can run first. Prefer the header cart control as
   * the focus trigger when present (stable vs off-screen rail buttons).
   */
  ByobBundleController.prototype._openCartDrawerAfterByobAddMobile = function (fallbackTrigger) {
    this._setStickyPanelOpen(false);
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') {
      var cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
      window.location.href = cartUrl;
      return;
    }
    var icon = document.querySelector('#cart-icon-bubble');
    var trigger = icon || fallbackTrigger;
    window.setTimeout(function () {
      drawer.open(trigger);
    }, 0);
  };

  ByobBundleController.prototype._onBuildCtaClick = function (e) {
    var btn = e.currentTarget;
    if (btn !== this.buildCta && btn !== this.buildCtaMobile) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    if (this._buildInFlight) return;
    e.preventDefault();

    var built = this._buildCartItemsFromPicks();
    if (built.error) {
      this._byobNotifyUser(built.error);
      return;
    }
    var items = built.items;
    if (!items.length) return;

    var self = this;
    this._buildInFlight = true;
    if (this.buildCta) {
      this.buildCta.setAttribute('disabled', '');
      this.buildCta.setAttribute('aria-disabled', 'true');
    }
    if (this.buildCtaMobile) {
      this.buildCtaMobile.setAttribute('disabled', '');
      this.buildCtaMobile.setAttribute('aria-disabled', 'true');
    }

    var finish = function () {
      self._buildInFlight = false;
      self._syncBuildCta();
    };

    var subscribeDiscountCode =
      self._isSubscribeMode() ? self._subscribeDiscountCodeForPack() : null;

    /**
     * Attach the pack’s BYOB subscribe code before `/cart/add.js` so the code is already on the
     * cart when UpCart (or anything else) refetches after add. Empty-cart codes sit as
     * applicable:false until lines land, then Shopify applies them immediately.
     */
    var ensureSubscribeDiscount = function () {
      if (!subscribeDiscountCode) return Promise.resolve(null);
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ discount: subscribeDiscountCode }),
      })
        .then(function (updRes) {
          return updRes.json();
        })
        .catch(function (err) {
          if (pbByobDebugEnabled()) {
            console.warn('[pb-byob-bundle] BYOB subscribe discount cart/update (pre-add) failed', err);
          }
          return null;
        });
    };

    var tryAdd = function () {
      return ensureSubscribeDiscount()
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items: items }),
          });
        })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (result) {
          var data = result.data;
          if (result.ok && data && !data.status) {
            var cartUpdateName =
              typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate
                ? PUB_SUB_EVENTS.cartUpdate
                : 'cart-update';

            var publishAndOpenUi = function (cartPayload) {
              if (typeof publish === 'function') {
                publish(cartUpdateName, { source: 'pb-byob', cartData: cartPayload });
              }
              var drawer = document.querySelector('cart-drawer');
              if (self._isPbByobMobileViewport()) {
                self._openCartDrawerAfterByobAddMobile(btn);
              } else if (drawer && typeof drawer.open === 'function') {
                drawer.open(btn);
              } else {
                var cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
                window.location.href = cartUrl;
              }
            };

            return fetch('/cart.js')
              .then(function (r) {
                return r.json();
              })
              .catch(function () {
                return data;
              })
              .then(function (cartPayload) {
                publishAndOpenUi(cartPayload || data);
              });
          }
          var errMsg =
            (data && (data.description || data.message)) ||
            'Could not add your box to the cart. Please try again.';
          self._byobNotifyUser(errMsg);
          if (pbByobDebugEnabled()) {
            console.warn('[pb-byob-bundle] cart/add failed', data);
          }
        })
        .catch(function () {
          self._byobNotifyUser('Could not add your box to the cart. Check your connection and try again.');
          if (pbByobDebugEnabled()) {
            console.warn('[pb-byob-bundle] cart/add network error');
          }
        })
        .then(finish);
    };

    fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var hasBundle =
          cart &&
          cart.items &&
          cart.items.some(function (item) {
            return item.properties && item.properties._browniebundle_parent === true;
          });
        if (hasBundle) {
          self._byobNotifyUser(
            'Your cart has a bundle that must be checked out separately. Clear the cart or finish that order first.'
          );
          finish();
          return;
        }
        return tryAdd();
      })
      .catch(function () {
        return tryAdd();
      });
  };

  ByobBundleController.prototype._bindPickerTitleScrollChrome = function () {
    if (this._pickerTitleChromeBound) return;
    var grid = this.pickerGridEl;
    var row = this.pickerTitleRowEl;
    if (!grid || !row) return;
    this._pickerTitleChromeBound = true;
    grid.addEventListener('scroll', this._onPickerTitleScrollChrome, { passive: true });
    if (typeof window.ResizeObserver === 'function') {
      this._pickerGridResizeObserver = new window.ResizeObserver(this._onPickerTitleScrollChrome);
      this._pickerGridResizeObserver.observe(grid);
    } else {
      window.addEventListener('resize', this._onPickerTitleScrollChrome);
    }
    this._onPickerTitleScrollChrome();
  };

  /** Primary build action: label + disabled state from `packSize` vs `picks.length`. */
  ByobBundleController.prototype._syncBuildCta = function () {
    var packSize = this.packSize;
    var n = this.picks.length;
    var remaining = packSize > 0 ? Math.max(0, packSize - n) : 0;
    var complete = packSize > 0 && n >= packSize;
    this._syncPriceLabels();
    this._syncRailMobilePriceBanner();
    this._syncSubscribePackSavings();
    this._syncOnetimePackSavings();
    var totalsFreq = this._totalsForFrequencyLabels();
    var modeSub = this._isSubscribeMode();
    var rowCta = this._manualPricingRow(packSize);
    var verbatimPrice =
      rowCta && this._manualFrequencyDisplayActive(packSize)
        ? modeSub
          ? String(rowCta.frequency_subscribe_price == null ? '' : rowCta.frequency_subscribe_price).trim()
          : String(rowCta.frequency_onetime_price == null ? '' : rowCta.frequency_onetime_price).trim()
        : '';
    var moneyStr = '';
    if (complete) {
      if (verbatimPrice !== '') {
        moneyStr = verbatimPrice;
      } else {
        moneyStr =
          modeSub && totalsFreq.subscribe > 0
            ? pbByobFormatMoneyCents(totalsFreq.subscribe)
            : !modeSub && totalsFreq.oneTime > 0
              ? pbByobFormatMoneyCents(totalsFreq.oneTime)
              : '';
      }
    }
    var mainLabel = complete ? (moneyStr ? moneyStr + ' | ADD TO CART' : 'ADD TO CART') : 'Add ' + String(remaining) + ' more';
    var stickyLabel = complete ? 'ADD TO CART' : 'Add more';

    this._setBuildButtonState(this.buildCta, mainLabel, complete);
    this._setBuildButtonState(this.buildCtaMobile, mainLabel, complete);
    this._setStickyBuildButtonState(stickyLabel, complete);
    this._syncStickyBuildCounts(n, packSize);
  };

  ByobBundleController.prototype.apply = function () {
    var root = this.root;
    if (this.packFieldset) {
      var psDom = readPackSizeFromFieldset(this.packFieldset);
      if (VALID_PACK[psDom]) this.packSize = psDom;
    }
    var packSize = this.packSize;
    var picks = this.picks;
    var n = picks.length;

    var counts = {};
    for (var c = 0; c < picks.length; c++) {
      var vid = picks[c].variantId;
      counts[vid] = (counts[vid] || 0) + 1;
    }

    root.querySelectorAll('[data-pb-bundle-flavor-card]').forEach(function (card) {
      if (!root.contains(card)) return;
      var cell = card.closest('.pb-byob-product-grid__cell');
      if (!cell) return;
      var vid = cell.getAttribute('data-variant-id');
      if (!vid) return;
      var qty = counts[String(vid)] || 0;
      var qtyEl = card.querySelector('[data-pb-bundle-flavor-card-qty]');
      if (qtyEl) qtyEl.textContent = String(qty);
      card.classList.toggle('pb-bundle-flavor-card--active', qty > 0);
      var dec = card.querySelector('.pb-bundle-flavor-card__stepper-btn--dec');
      var inc = card.querySelector('.pb-bundle-flavor-card__stepper-btn--inc');
      if (dec) dec.disabled = qty <= 0;
      if (inc) inc.disabled = n >= packSize;
    });

    if (this.summary) {
      this._syncProgressVisual();
      var cur = this.summary.querySelector('.pb-byob__selected-count-current');
      var maxEl = this.summary.querySelector('.pb-byob__selected-count-max');
      if (cur) cur.textContent = String(n);
      if (maxEl) maxEl.textContent = String(packSize);
      this.summary.setAttribute('data-pb-byob-pack-size', String(packSize));
    }

    var pillCur = root.querySelector('.build-your-box__picker-count-pill-current');
    var pillMax = root.querySelector('.build-your-box__picker-count-pill-max');
    if (pillCur) pillCur.textContent = String(n);
    if (pillMax) pillMax.textContent = String(packSize);

    if (this.slotsParent) {
      var slotCountVar = Math.min(packSize, MAX_PACK);
      this.slotsParent.style.setProperty('--pb-byob-selected-slot-count', String(slotCountVar));
      this.slotsParent.style.setProperty(
        '--pb-byob-selected-cols-desktop',
        String(pbByobDesktopSummaryCols(packSize))
      );
    }

    for (var s = 0; s < this.slotEls.length; s++) {
      var li = this.slotEls[s];
      var slotNum = s + 1;
      if (slotNum > packSize) {
        li.setAttribute('hidden', '');
        li.setAttribute('aria-hidden', 'true');
        li.classList.add('pb-byob__selected-slot--beyond-pack');
        continue;
      }
      li.removeAttribute('hidden');
      li.removeAttribute('aria-hidden');
      li.classList.remove('pb-byob__selected-slot--beyond-pack');

      if (s < picks.length) {
        li.classList.remove('pb-byob__selected-slot--empty');
        li.classList.add('pb-byob__selected-slot--filled');
        li.innerHTML = this._filledInnerHTML(picks[s]);
      } else {
        li.classList.remove('pb-byob__selected-slot--filled');
        li.classList.add('pb-byob__selected-slot--empty');
        li.innerHTML = this._emptyInnerHTML();
      }
    }

    this._syncBuildCta();
    this._syncStickyPanelContent();
    this._onPickerTitleScrollChrome();
    this._onStickyVisibilityFallback();

    this._syncCategoryTabVisibilityFromGrid();
    this._syncPostCtaMixCategoryVisibility();
    this._syncPostCtaMixCounts();
    this._applyCategoryFilter(this._getActiveCategoryKeyFromDom());
    this._syncCategoryScrollHints();

    var nowComplete = packSize > 0 && n >= packSize;
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 989px)').matches &&
      nowComplete &&
      !this._wasComplete &&
      this.stickyBuildWrap
    ) {
      var selfAo = this;
      this._setStickyBuildVisible(true, true);
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            var stillComplete = selfAo.packSize > 0 && selfAo.picks.length >= selfAo.packSize;
            if (stillComplete) selfAo._setStickyPanelOpen(true);
          });
        });
      } else {
        this._setStickyPanelOpen(true);
      }
    }
    this._wasComplete = nowComplete;

    root.dispatchEvent(
      new CustomEvent('pb:byob-selection-change', {
        bubbles: false,
        detail: { packSize: packSize, picks: picks.slice() },
      })
    );
  };

  ByobBundleController.prototype.bind = function () {
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('pb:pack-size-change', this._onPackChange);
    if (this.buildCta) {
      this.buildCta.addEventListener('click', this._onBuildCtaClick);
    }
    if (this.buildCtaMobile) {
      this.buildCtaMobile.addEventListener('click', this._onBuildCtaClick);
    }
    if (this.stickyBuildCta) {
      this.stickyBuildCta.addEventListener('click', this._onStickyBuildCtaClick);
    }
    if (this.stickyBuildToggle) {
      this.stickyBuildToggle.addEventListener('keydown', this._onStickyToggleKeydown);
    }
    if (this.stickyBackdrop) {
      this.stickyBackdrop.addEventListener('click', this._onStickyBackdropClick);
    }
    if (this.stickyBuildWrap) {
      this._setStickyBuildVisible(false, false);
    }
    var self = this;
    function onFrequencyFieldsetChange(ev) {
      self._mirrorFrequencyFieldsets(ev.currentTarget);
      self._onFrequencyChange();
      self._syncBuildCta();
    }
    if (this.frequencyFieldsetRail) {
      this.frequencyFieldsetRail.addEventListener('change', onFrequencyFieldsetChange);
    }
    if (this.frequencyFieldsetMobile) {
      this.frequencyFieldsetMobile.addEventListener('change', onFrequencyFieldsetChange);
    }
    if (this.frequencyFieldsetRail && this.frequencyFieldsetMobile) {
      this._mirrorFrequencyFieldsets(this.frequencyFieldsetRail);
    }
    this._syncFrequencyDeliveryNote();
    this._syncPackBadgesMode();
    if (typeof window.matchMedia === 'function') {
      var mqWide = window.matchMedia('(min-width: 990px)');
      if (mqWide.addEventListener) mqWide.addEventListener('change', this._onViewport);
      else if (mqWide.addListener) mqWide.addListener(this._onViewport);
    }
    this.apply();
    this._bindStickySummaryObserver();
    this._onStickyVisibilityFallback();
    this._bindPickerTitleScrollChrome();
    this._bindCategoryScrollHints();
    var self = this;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        self._syncProgressVisual();
        self._onPickerTitleScrollChrome();
        self._syncCategoryScrollHints();
        window.requestAnimationFrame(function () {
          self._syncProgressVisual();
          self._onPickerTitleScrollChrome();
          self._syncCategoryScrollHints();
        });
      });
    }
  };

  ByobBundleController.prototype._onViewport = function () {
    if (!this._isPbByobMobileViewport()) {
      if (this._stickyPanelCloseTimer) {
        clearTimeout(this._stickyPanelCloseTimer);
        this._stickyPanelCloseTimer = null;
      }
      if (this.stickyBuildPanel) {
        this.stickyBuildPanel.removeEventListener(
          'transitionend',
          this._handleStickyPanelClosedTransitionEndBound
        );
      }
      this._stickyPanelAnimatingClose = false;
      this._setStickyPanelOpen(false);
    }

    this._syncProgressVisual();
    this._onStickyVisibilityFallback();
    this._syncCategoryScrollHints();
    if (
      this.stickyBackdrop &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 990px)').matches
    ) {
      this.stickyBackdrop.hidden = true;
      this.stickyBackdrop.setAttribute('aria-hidden', 'true');
    }
  };

  function initAll() {
    document.querySelectorAll('[data-pb-byob-bundle-root]').forEach(function (root) {
      if (root.__pbByobBundle) return;
      var ctrl = new ByobBundleController(root);
      root.__pbByobBundle = ctrl;
      ctrl.bind();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    var target = ev.target;
    if (!target || !target.querySelectorAll) return;
    target.querySelectorAll('[data-pb-byob-bundle-root]').forEach(function (root) {
      if (root.__pbByobBundle) return;
      var ctrl = new ByobBundleController(root);
      root.__pbByobBundle = ctrl;
      ctrl.bind();
    });
  });
})();
